/**
 * The tenancy switcher's pure half — AG-2415, the last of AG-1543's six points.
 *
 * A person who belongs to two organisations could CHOOSE one at sign-in (AG-2207) and then had no way to change it
 * short of ending their whole suite session. Identity Studio can already re-mint a session for another of the
 * person's tenancies (`POST /iam/sdk/auth/switch-tenant`, which also ends the session switched from) and list them
 * (`available_tenancies` on `GET /iam/sdk/auth/my-sessions`). Both are authenticated by the APPLICATION's SDK secret
 * plus the person's own bearer, so a browser cannot call them: each application's backend relays them as
 * `GET /auth/tenancies` and `POST /auth/switch-tenancy` (the launcher's lesson — check what a page can reach before
 * assuming). This file reads those two answers; `tenancy-switcher.tsx` renders them, once, for every application.
 *
 * ⛔ Framework-free, like `core.ts`: it must load in a node test with no `window`.
 */
/** Reads an application's `GET /auth/tenancies` answer: `{tenancies: [{application_tenant_id, organization_name,
 *  role, is_current}]}`. Anything malformed is dropped rather than rendered; a nameless organisation reads as such
 *  rather than as an empty row. */
export function readTenancies(payload) {
    const list = payload?.tenancies;
    if (!Array.isArray(list))
        return [];
    const out = [];
    for (const raw of list) {
        const row = raw;
        const id = row?.application_tenant_id;
        if (typeof id !== 'string' || !id)
            continue;
        const name = typeof row?.organization_name === 'string' && row.organization_name.trim()
            ? row.organization_name.trim()
            : 'Unnamed organization';
        out.push({
            applicationTenantId: id,
            organizationName: name,
            role: typeof row?.role === 'string' && row.role ? row.role : null,
            isCurrent: row?.is_current === true,
        });
    }
    return out;
}
/** Whether there is anything to switch between. Most people hold exactly one tenancy, and Identity Studio documents
 *  an empty or single list as a real answer: the switcher then renders NOTHING rather than a menu of one. */
export function tenancySwitcherIsWorthShowing(tenancies) {
    return tenancies.length >= 2;
}
/** The tenancy in force, if the list marks one. */
export function currentTenancy(tenancies) {
    return tenancies.find((t) => t.isCurrent) ?? null;
}
export const NOT_YOURS_MESSAGE = "You can't switch to that organization any more — it may have removed you. The list has been refreshed.";
export const SESSION_ENDED_MESSAGE = 'Your session has ended. Sign in again to switch organization.';
export const SWITCH_FAILED_MESSAGE = "Couldn't switch organization. Please try again.";
export const LIST_FAILED_MESSAGE = "Couldn't load your organizations.";
function errorCode(body) {
    const b = body;
    if (typeof b?.error === 'string')
        return b.error;
    const d = b?.detail;
    if (d && typeof d === 'object') {
        if (typeof d.error === 'string')
            return d.error;
        if (typeof d.code === 'string')
            return d.code;
    }
    return null;
}
/**
 * What an application's `POST /auth/switch-tenancy` answer means to the person.
 *
 * * 2xx — switched; `bundle` is the application's own body, handed back to the host to apply.
 * * 409 `already_in_tenancy` — a double click, or a second tab that already switched: nothing to do, and NOT an error.
 * * 403 `not_your_tenancy` — the server refused a tenancy that is not theirs (a membership withdrawn since the list
 *   was read). Said in words, never as a generic failure (AG-2415 acceptance 3).
 * * 401 — the session itself has ended.
 */
export function readSwitchOutcome(status, body) {
    if (status >= 200 && status < 300)
        return { kind: 'switched', bundle: body };
    const code = errorCode(body);
    if (status === 409 || code === 'already_in_tenancy')
        return { kind: 'already' };
    if (status === 403 && code === 'not_your_tenancy')
        return { kind: 'refused', message: NOT_YOURS_MESSAGE };
    if (status === 401)
        return { kind: 'failed', message: SESSION_ENDED_MESSAGE };
    return { kind: 'failed', message: SWITCH_FAILED_MESSAGE };
}
